import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    _mfq: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class MouseflowService {
  private initialized = false;

  constructor(private readonly router: Router) {}

  /**
   * Initializes Mouseflow tracking by injecting the CDN script and
   * hooking into Angular's Router to send virtual pageviews on every
   * route change (SPA support).
   *
   * Call once, passing the Website ID from your Mouseflow account.
   * The ID is read from app.config.json → "mouseflowWebsiteId".
   */
  initialize(websiteId: string): void {
    console.log('[Mouseflow] Mouseflow client initialized.');
    if (this.initialized || !websiteId) {
      return;
    }

    // Ensure the command queue exists before the CDN script is fetched
    window._mfq = window._mfq || [];

    // ── Disable all masking so session replays show full content ──
    window._mfq.push(['config', 'autoMasking', false]);
    window._mfq.push(['config', 'recordContent', true]);
    window._mfq.push(['config', 'maskFields', false]);
    window._mfq.push(['config', 'maskInputs', false]);
    window._mfq.push(['config', 'htmlMasking', false]);
    window._mfq.push(['config', 'recordKeystrokes', true]);

    // Dynamically inject the Mouseflow CDN tracking script
    const mf = document.createElement('script');
    mf.type = 'text/javascript';
    mf.defer = true;
    mf.src = `//cdn.mouseflow.com/projects/${websiteId}.js`;
    document.getElementsByTagName('head')[0].appendChild(mf);

    // Track Angular route changes as virtual pageviews
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.newPageView(event.urlAfterRedirects);
      });

    this.initialized = true;
  }

  /**
   * Push a virtual pageview — essential for SPAs where the browser URL
   * changes without a full reload.
   */
  newPageView(path: string): void {
    window._mfq = window._mfq || [];
    window._mfq.push(['newPageView', path]);
  }

  /**
   * Tag the current session with a short label.
   * Tags are page-scoped and visible in Session Replay / Heatmaps.
   * e.g. "site-selected", "dashboard-opened"
   */
  tag(tagName: string): void {
    window._mfq = window._mfq || [];
    window._mfq.push(['tag', tagName]);
  }

  /**
   * Set a session-scoped key/value variable.
   * Variables can be used to filter sessions in Mouseflow.
   * ⚠️ Do NOT pass any personally-identifiable information.
   */
  setVariable(key: string, value: string | number | boolean): void {
    window._mfq = window._mfq || [];
    window._mfq.push(['setVariable', key, String(value)]);
  }

  /**
   * Record the current user by their opaque (non-PII) identifier.
   * Stored as a session variable "userId".
   */
  identifyUser(userId: string): void {
    this.setVariable('userId', userId);
  }

  /**
   * Store the active site as session variables so sessions can be
   * segmented by site in Mouseflow dashboards.
   */
  trackSiteContext(siteId: string, siteTitle: string): void {
    this.setVariable('currentSiteId', siteId);
    this.setVariable('currentSiteTitle', siteTitle);
  }

  /**
   * Fire a "site-selected" tag and update site context variables
   * whenever the user explicitly picks a new site.
   */
  trackSiteSelected(siteId: string, siteTitle: string): void {
    this.tag('site-selected');
    this.trackSiteContext(siteId, siteTitle);
  }

  /**
   * Start (or resume) recording the current session.
   * Call this when entering a high-value area such as the document editor
   * to ensure the session is always captured, even if global sampling is
   * set to less than 100 %.
   */
  startRecording(): void {
    window._mfq = window._mfq || [];
    window._mfq.push(['start']);
  }

  /**
   * Pause recording for the current session.
   * Call this when the editor is destroyed so that post-editor navigation
   * (e.g. browse/search views) is not captured unless desired.
   */
  stopRecording(): void {
    window._mfq = window._mfq || [];
    window._mfq.push(['stop']);
  }

  /**
   * Opt the current session OUT of recording entirely and permanently.
   * Use this only for admin / support users whose sessions should never
   * appear in Mouseflow dashboards.
   */
  optOut(): void {
    window._mfq = window._mfq || [];
    window._mfq.push(['optOut']);
  }

  /**
   * Submit a custom event visible in the Mouseflow Timeline view.
   * Use this for discrete user actions that are not captured by tags
   * (e.g. 'citation-inserted', 'track-changes-accepted').
   */
  recordEvent(eventName: string): void {
    window._mfq = window._mfq || [];
    window._mfq.push(['recordEvent', eventName]);
  }

  /**
   * Mark a DOM element (and its descendants) so that Mouseflow does NOT
   * mask their text content.  Particularly useful for CKEditor's
   * contenteditable area which Mouseflow treats as a sensitive input
   * by default.
   *
   * Uses the `data-recording-disable="mask"` attribute that Mouseflow
   * recognises as an opt-out from text masking for that subtree.
   */
  unmasElement(element: HTMLElement): void {
    if (element) {
      element.setAttribute('data-recording-disable', 'mask');
    }
  }
}
